
import { developmentDetector } from './developmentModeDetector';

interface PerformanceMetrics {
  lcp?: number;
  fid?: number;
  cls?: number;
  inp?: number;
  fcp?: number;
  performanceScore: number;
  memoryUsage?: number;
  errors: number;
  warnings: number;
  renderTime: number;
  renderCount: number;
  loadTime: number;
}

interface ComponentMetrics {
  [componentName: string]: {
    renderTime: number;
    renderCount: number;
    lastRender: number;
  };
}

class UnifiedPerformanceMonitor {
  private static instance: UnifiedPerformanceMonitor;
  private metrics: PerformanceMetrics = {
    performanceScore: 100,
    errors: 0,
    warnings: 0,
    renderTime: 0,
    renderCount: 0,
    loadTime: 0
  };
  private componentMetrics: ComponentMetrics = {};
  private observers: Map<string, PerformanceObserver> = new Map();
  private isInitialized = false;
  private config = developmentDetector.getConfig();

  static getInstance(): UnifiedPerformanceMonitor {
    if (!UnifiedPerformanceMonitor.instance) {
      UnifiedPerformanceMonitor.instance = new UnifiedPerformanceMonitor();
    }
    return UnifiedPerformanceMonitor.instance;
  }

  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    
    // Minimal initialization - only essential metrics
    this.recordLoadTime();
    
    // Only enable full monitoring in production when explicitly needed
    if (!this.config.isDevelopment) {
      this.startCoreWebVitalsObserver();
      // Skip memory monitoring to reduce overhead
    }
  }

  private startCoreWebVitalsObserver(): void {
    if (!('PerformanceObserver' in window)) return;

    try {
      // Single observer for all Core Web Vitals
      const vitalsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.processPerformanceEntry(entry);
        }
      });

      // Observe all relevant entry types at once
      const entryTypes = ['largest-contentful-paint', 'first-input', 'layout-shift', 'paint'];
      vitalsObserver.observe({ entryTypes });
      this.observers.set('vitals', vitalsObserver);

    } catch (error) {
      // Silently handle unsupported browsers
    }
  }

  private processPerformanceEntry(entry: PerformanceEntry): void {
    switch (entry.entryType) {
      case 'largest-contentful-paint':
        this.metrics.lcp = entry.startTime;
        this.checkLCPThreshold(entry.startTime);
        break;
      
      case 'first-input':
        const fid = (entry as any).processingStart - entry.startTime;
        this.metrics.fid = fid;
        this.checkFIDThreshold(fid);
        break;
      
      case 'layout-shift':
        if (!(entry as any).hadRecentInput) {
          this.metrics.cls = (this.metrics.cls || 0) + (entry as any).value;
        }
        break;
      
      case 'paint':
        if (entry.name === 'first-contentful-paint') {
          this.metrics.fcp = entry.startTime;
        }
        break;
    }
    
    this.updatePerformanceScore();
  }

  private checkLCPThreshold(lcp: number): void {
    if (lcp > 4000) {
      this.recordWarning();
    }
  }

  private checkFIDThreshold(fid: number): void {
    if (fid > 300) {
      this.recordWarning();
    }
  }

  private recordLoadTime(): void {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const loadTime = performance.now();
      this.metrics.loadTime = loadTime;
    }
  }

  private startMemoryMonitoring(): void {
    if (!this.config.isProduction || !('memory' in performance)) return;

    // Check memory every 10 minutes in production only
    setInterval(() => {
      const memory = (performance as any).memory;
      this.metrics.memoryUsage = memory.usedJSHeapSize / 1024 / 1024; // MB
      
      if (memory.usedJSHeapSize / memory.jsHeapSizeLimit > 0.9) {
        this.recordWarning();
      }
    }, 10 * 60 * 1000);
  }

  private updatePerformanceScore(): void {
    let score = 100;
    
    if (this.metrics.lcp) {
      if (this.metrics.lcp > 4000) score -= 30;
      else if (this.metrics.lcp > 2500) score -= 15;
    }
    
    if (this.metrics.fid) {
      if (this.metrics.fid > 300) score -= 25;
      else if (this.metrics.fid > 100) score -= 10;
    }
    
    if (this.metrics.cls && this.metrics.cls > 0.25) score -= 20;
    else if (this.metrics.cls && this.metrics.cls > 0.1) score -= 10;
    
    this.metrics.performanceScore = Math.max(0, score);
  }

  // Lightweight component performance tracking
  startComponentRender(componentName: string): void {
    // Skip in development for faster dev experience
    if (this.config.isDevelopment) return;
    
    // Only track critical components to reduce overhead
    if (!this.isComponentCritical(componentName)) return;
    
    if (!this.componentMetrics[componentName]) {
      this.componentMetrics[componentName] = {
        renderTime: 0,
        renderCount: 0,
        lastRender: 0
      };
    }
    
    this.componentMetrics[componentName].lastRender = performance.now();
  }

  endComponentRender(componentName: string): void {
    if (this.config.isDevelopment) return;
    
    const component = this.componentMetrics[componentName];
    if (component && component.lastRender > 0) {
      const renderTime = performance.now() - component.lastRender;
      component.renderTime = renderTime;
      component.renderCount += 1;
      
      // Only update global metrics for critical components
      if (this.isComponentCritical(componentName)) {
        this.metrics.renderTime = renderTime;
        this.metrics.renderCount += 1;
      }
    }
  }

  private isComponentCritical(componentName: string): boolean {
    // Only track performance for critical page components
    const criticalComponents = [
      'ProductPageTemplate',
      'EnhancedProductCard', 
      'CheckoutWizard',
      'Navigation'
    ];
    return criticalComponents.includes(componentName);
  }

  getComponentRenderTime(componentName: string): number {
    return this.componentMetrics[componentName]?.renderTime || 0;
  }

  getComponentRenderCount(componentName: string): number {
    return this.componentMetrics[componentName]?.renderCount || 0;
  }

  // Web Vitals compatibility methods
  getWebVitalsMetrics(): { lcp: { value: number; status: 'good' | 'needs-improvement' | 'poor' }; fid: { value: number; status: 'good' | 'needs-improvement' | 'poor' }; cls: { value: number; status: 'good' | 'needs-improvement' | 'poor' }; fcp: { value: number; status: 'good' | 'needs-improvement' | 'poor' } } {
    const safeLcp = this.metrics?.lcp ?? 0;
    const safeFid = this.metrics?.fid ?? 0;
    const safeCls = this.metrics?.cls ?? 0;
    const safeFcp = this.metrics?.fcp ?? 0;
    
    return {
      lcp: {
        value: safeLcp,
        status: this.getMetricStatus('LCP', safeLcp)
      },
      fid: {
        value: safeFid,
        status: this.getMetricStatus('FID', safeFid)
      },
      cls: {
        value: safeCls,
        status: this.getMetricStatus('CLS', safeCls)
      },
      fcp: {
        value: safeFcp,
        status: this.getMetricStatus('FCP', safeFcp)
      }
    };
  }

  getMetricStatus(metricName: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      FID: { good: 100, poor: 300 },
      CLS: { good: 0.1, poor: 0.25 },
      FCP: { good: 1800, poor: 3000 }
    };

    const threshold = thresholds[metricName as keyof typeof thresholds];
    if (!threshold) return 'good';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }

  recordError(): void {
    this.metrics.errors++;
    this.updatePerformanceScore();
  }

  recordWarning(): void {
    this.metrics.warnings++;
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  getHealthStatus(): 'healthy' | 'degraded' | 'critical' {
    const { performanceScore, errors } = this.metrics;
    
    if (errors > 5 || performanceScore < 50) return 'critical';
    if (errors > 2 || performanceScore < 75) return 'degraded';
    return 'healthy';
  }

  // Compatibility methods for replaced hooks
  refresh(): void {
    // Reset metrics and reinitialize
    this.metrics = {
      performanceScore: 100,
      errors: 0,
      warnings: 0,
      renderTime: 0,
      renderCount: 0,
      loadTime: 0
    };
    this.recordLoadTime();
  }

  cleanup(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.isInitialized = false;
  }
}

export const unifiedPerformanceMonitor = UnifiedPerformanceMonitor.getInstance();
